import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, FileText, Download, Trash2, X } from "lucide-react";
import api, { apiError, fmtDate, downloadBlob } from "../api";
import { Button, Badge } from "./ui";

export default function DocUploadSlot({ candidateId, category, label, hint, accept, docs = [], canUpload, isAdmin, onChanged, onDelete, revisit = false }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const active = docs.filter((d) => !d.superseded);
  const superseded = docs.filter((d) => d.superseded);

  const submit = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("category", category);
    fd.append("file", file);
    if (revisit) fd.append("revisit", "true");
    setBusy(true);
    try {
      await api.post(`/candidates/${candidateId}/documents`, fd);
      toast.success(`${label} submitted`);
      setFile(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const renderDoc = (d, isOld) => (
    <li key={d.id} className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm ${isOld ? "border-slate-100 bg-slate-50 opacity-60" : "border-slate-100 bg-slate-50"}`} data-testid="document-item">
      <FileText size={14} className="shrink-0 text-teal" />
      <span className="truncate font-medium text-slate-800">{d.original_filename}</span>
      {isOld && <Badge color="slate">superseded</Badge>}
      <span className="ml-auto shrink-0 text-xs text-slate-400">{fmtDate(d.created_at)} · {d.uploaded_by_name}</span>
      <button
        onClick={() => downloadBlob(`/documents/${d.id}/download`, d.original_filename)}
        className="shrink-0 rounded p-1 text-slate-500 transition-colors hover:bg-teal-light hover:text-teal"
        title="Download"
        data-testid={`download-doc-${d.id}`}
      >
        <Download size={15} />
      </button>
      {isAdmin && onDelete && (
        <button onClick={() => onDelete(d.id)} className="shrink-0 rounded p-1 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`delete-doc-${d.id}`}>
          <Trash2 size={15} />
        </button>
      )}
    </li>
  );

  return (
    <div data-testid={`doc-section-${category}`}>
      <div className="mb-2 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-slate-700">{label}</p>
          <p className="text-[11px] text-slate-400">{hint}</p>
        </div>
      </div>
      {active.length === 0 && superseded.length === 0 && !canUpload && (
        <p className="text-xs text-slate-400">No file uploaded</p>
      )}
      <ul className="space-y-1.5">
        {active.map((d) => renderDoc(d, false))}
        {superseded.map((d) => renderDoc(d, true))}
      </ul>
      {canUpload && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => { setFile(e.target.files[0] || null); e.target.value = ""; }}
            data-testid={`upload-input-${category}`}
          />
          <Button size="sm" variant="outline" onClick={() => inputRef.current.click()} disabled={busy} data-testid={`choose-file-${category}`}>
            <Upload size={14} /> {file ? file.name.slice(0, 28) : "Choose file"}
          </Button>
          {file && (
            <button
              onClick={() => setFile(null)}
              className="rounded-full border border-slate-200 bg-white p-1 text-slate-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
              title="Remove selected file"
              data-testid={`clear-file-${category}`}
            >
              <X size={13} />
            </button>
          )}
          <Button size="sm" onClick={submit} disabled={!file || busy} data-testid={`submit-doc-${category}`}>
            {busy ? "Uploading..." : "Submit"}
          </Button>
        </div>
      )}
    </div>
  );
}
